/**
 * FOLLOWING ONE REVIEW RUN — reading a portfolio's queued review off the
 * triggered-events list.
 *
 * The list holds every run of the last 24 hours, old ones included, and a run
 * just queued may not be in it yet. So "the newest row says DONE" is not "the
 * run I asked for is done": straight after a request the newest row can be
 * yesterday's. A finished row counts only once it is known to be the run being
 * followed — it was not there before the request, or it has been seen waiting
 * or running since.
 */

/** The event a portfolio review is queued as. */
export const REVIEW_RUN = 'portfolio.review.run';

const ACTIVE_STATES = new Set(['WAITING', 'RUNNING']);

/** The most recently queued review run in a list of triggered events, or null. */
export const newestReviewRun = (rows) => (rows ?? [])
  .filter((r) => r?.eventType === REVIEW_RUN)
  .slice()
  /* Newest first as served; the sort only settles a list that is not. */
  .sort((a, b) => (Date.parse(b.enqueuedAt ?? '') || 0) - (Date.parse(a.enqueuedAt ?? '') || 0))[0] ?? null;

/** Whether a run is still queued or under way. */
export const isReviewRunActive = (row) => Boolean(row) && ACTIVE_STATES.has(row.state) && !row.cancelled;

/**
 * What one look at the list says about the run being followed.
 *
 * `baseline` is the newest run as it stood BEFORE the request: a row, null
 * when there was none, or undefined when that could not be read — in which
 * case only a row seen active can be trusted to be the new one. `seenActive`
 * carries that from one look to the next.
 *
 * Answers `{ step, seenActive }`, where `step` is:
 *   wait     nothing to report yet — not listed, still queued, or still the old run
 *   done     the review was written
 *   failed   the run failed
 *   stopped  cancelled, or a state that says nothing — stop following, quietly
 */
export function reviewRunStep(row, { baseline, seenActive = false } = {}) {
  if (!row) return { step: 'wait', seenActive };
  if (isReviewRunActive(row)) return { step: 'wait', seenActive: true };
  const isNew = baseline !== undefined
    && (!baseline || row.key !== baseline.key || row.enqueuedAt !== baseline.enqueuedAt);
  if (!seenActive && !isNew) return { step: 'wait', seenActive };
  if (row.state === 'DONE') return { step: 'done', seenActive };
  if (row.state === 'FAILED') return { step: 'failed', seenActive };
  return { step: 'stopped', seenActive };
}
